'use client';

import { useCallback, useRef, useState } from 'react';

import HeaderMobileDrawer from './HeaderMobileDrawer';
import HeaderMobileSearchSheet from './HeaderMobileSearchSheet';
import HeaderNavBar from './HeaderNavBar';
import HeaderTopBar from './HeaderTopBar';
import { GLOBAL_NAV_ITEMS } from '../../lib/header-config';
import type { SearchComboboxConfig } from '@/shared/components/search-combobox';
import { useNavbarContext } from './context';
import { cn } from '@/shared/utils/cn';
import { WIDGETS_ASSET_HOST } from '@/shared/config/assets';
import useHeaderActiveItem from '../../hooks/header/useHeaderActiveItem';
import useHeaderCart from '../../hooks/header/useHeaderCart';
import useHeaderDropdown from '../../hooks/header/useHeaderDropdown';
import useHeaderMobileMenu from '../../hooks/header/useHeaderMobileMenu';
import useHeaderNavigation from '../../hooks/header/useHeaderNavigation';
import useHeaderOverlayEffects from '../../hooks/header/useHeaderOverlayEffects';
import useHeaderRouteEffects from '../../hooks/header/useHeaderRouteEffects';
import useHeaderSearch from '../../hooks/header/useHeaderSearch';
import useHeaderSearchSheet from '../../hooks/header/useHeaderSearchSheet';
import type { NavItem } from '../../types/navigation';

interface HeaderProps {
  logoHeight?: number;
  logoHref?: string;
  logoSrc?: string;
  logoWidth?: number;
  navItems?: NavItem[];
  pathname?: string;
  search?: SearchComboboxConfig;
  showSearch?: boolean;
  sticky?: boolean;
}

const Header = ({
  logoHeight = 355,
  logoHref = '/',
  logoSrc = `${WIDGETS_ASSET_HOST}/images/logo-adipa.png`,
  logoWidth = 1776,
  navItems: navItemsProp = GLOBAL_NAV_ITEMS,
  pathname: pathnameProp,
  search: searchProp,
  showSearch = true,
  sticky = true,
}: HeaderProps) => {
  const headerRef = useRef<HTMLElement>(null);
  const [mobileSearchOffsetTop, setMobileSearchOffsetTop] = useState(0);
  const { onCartClick } = useNavbarContext();

  const search = useHeaderSearch(searchProp);
  const { navItems, pathname } = useHeaderNavigation(navItemsProp, pathnameProp);

  const {
    closeDropdown,
    dropdownPosition,
    dropdownRef,
    openDropdown,
    openDropdownMenu,
    scheduleDropdownClose,
    setNavTriggerRef,
  } = useHeaderDropdown(navItems);

  const { closeCart, isCartOpen, openCart } = useHeaderCart();
  const { closeMobileMenu, isMobileOpen, openMobileMenu } = useHeaderMobileMenu();
  const { closeSearch, isSearchOpen, openSearch } = useHeaderSearchSheet();
  const openedItem = useHeaderActiveItem(navItems, openDropdown);

  const syncMobileSearchOffset = useCallback(() => {
    const headerTop = headerRef.current?.getBoundingClientRect().top ?? 0;

    setMobileSearchOffsetTop(Math.max(0, Math.round(headerTop)));
  }, []);

  const handleSearchOpen = useCallback(() => {
    closeCart();
    closeDropdown();
    closeMobileMenu();
    syncMobileSearchOffset();
    openSearch();
  }, [closeCart, closeDropdown, closeMobileMenu, openSearch, syncMobileSearchOffset]);

  const handleDropdownEnter = useCallback(
    (key: string) => {
      closeCart();
      openDropdownMenu(key);
    },
    [closeCart, openDropdownMenu],
  );

  const handleCartOpen = useCallback(() => {
    closeDropdown();
    openCart();
    void onCartClick?.();
  }, [closeDropdown, onCartClick, openCart]);

  useHeaderRouteEffects({
    onCartClose: closeCart,
    onDropdownClose: closeDropdown,
    pathname,
  });

  useHeaderOverlayEffects({
    dropdownRef,
    headerRef,
    isMobileOpen,
    onCartClose: closeCart,
    onDropdownClose: closeDropdown,
    onMobileClose: closeMobileMenu,
  });

  return (
    <>
      <header
        className={cn('global-header-root', sticky && 'global-header-sticky')}
        ref={headerRef}
      >
        <HeaderTopBar
          isCartOpen={isCartOpen}
          isMobileOpen={isMobileOpen}
          isSearchOpen={isSearchOpen}
          logoHeight={logoHeight}
          logoHref={logoHref}
          logoSrc={logoSrc}
          logoWidth={logoWidth}
          search={search}
          showSearch={showSearch}
          onCartOpen={handleCartOpen}
          onMobileOpen={openMobileMenu}
          onSearchOpen={handleSearchOpen}
        />

        {navItems.length > 0 && (
          <HeaderNavBar
            dropdownPosition={dropdownPosition}
            dropdownRef={dropdownRef}
            navItems={navItems}
            openDropdown={openDropdown}
            openedItem={openedItem}
            onDropdownClose={closeDropdown}
            onDropdownEnter={handleDropdownEnter}
            onDropdownLeave={scheduleDropdownClose}
            onNavTriggerRef={setNavTriggerRef}
          />
        )}
      </header>

      <HeaderMobileDrawer
        isOpen={isMobileOpen}
        navItems={navItems}
        onClose={closeMobileMenu}
      />

      {showSearch && (
        <HeaderMobileSearchSheet
          isOpen={isSearchOpen}
          offsetTop={mobileSearchOffsetTop}
          search={search}
          onClose={closeSearch}
        />
      )}
    </>
  );
};

export default Header;
